import { getCookie, setCookie } from "/services/cookies-service";
import { LAST_ACCOUNT_SLUG, WELCOME_BACK_SHOULD_UPDATE_CTA_COOKIE_NAME } from "constants/cookies";
import * as Sentry from "@sentry/nextjs";
import { shouldGetWelcomeBackPageTest } from "services/ab-tests/acquisition-ab-test-service";
import { getPlatformProductID, WORK_MANAGEMENT_PRODUCT_ID } from "constants/products";

export const ACTIVE_ACCOUNTS_SLUGS_COOKIE_NAME = "monday_active_account_slugs"; // set in the platform after each login expiry 1 year
const ALL_SLUGS_COOKIE_NAME = "dapulseAccountSlugs"; // set in the platform after each login - session cookie no expiration
export const SLUG_DETAILS_COOKIE_NAME = "monday_slug_details"; // set in the platform after each login - saves up to 5 last slugs

const WELCOME_BACK_COOKIE_EXPIRATION = 30;
export const DEFAULT_PLATFORM_USER_IMAGE_SOURCE = "https://cdn1.monday.com/dapulse_default_photo.png";

// Returns array of slug names (string)
export const getMondayActiveSlugs = () => {
  let slugsFromCookie = getCookie(ACTIVE_ACCOUNTS_SLUGS_COOKIE_NAME);
  let slugs = [];
  if (slugsFromCookie) {
    try {
      slugsFromCookie = decodeURIComponent(slugsFromCookie);
      slugs = JSON.parse(slugsFromCookie);
    } catch (err) {
      Sentry.captureException(err);
    }
  }
  return slugs;
};

export const getMondaySlugsDetails = () => {
  let slugsFromCookie = getCookie(SLUG_DETAILS_COOKIE_NAME);
  let parsedSlugsDetails = [];
  if (slugsFromCookie) {
    try {
      slugsFromCookie = decodeURIComponent(slugsFromCookie);
      const slugsDetails = JSON.parse(slugsFromCookie);
      parsedSlugsDetails = slugsDetails.map((slugDetail) => parseSingleAccountSlugDetails(slugDetail));
    } catch (err) {
      Sentry.captureException(err);
    }
  }
  return parsedSlugsDetails;
};

export const getLastUsedAccountSlug = () => {
  return getCookie(LAST_ACCOUNT_SLUG);
};

export const getMondayFullUrlForSlug = (slug) => {
  return `//${slug}.${process.env.NEXT_PUBLIC_AUTH_PLATFORM_DOMAIN}`; // works also in client side (next trick)
};

export const validateAllAccountsCreatedAfterDate = (date) => {
  try {
    const slugs = getMondaySlugsDetails();
    const newSlugs = slugs.filter((slug) => slug.ac && new Date(slug.ac) >= date);
    return newSlugs.length === slugs.length;
  } catch (err) {
    Sentry.captureException(err);
    return false;
  }
};

export const isExistingUserInWelcomeBackTest = (abTests) => {
  return (
    shouldGetWelcomeBackPageTest(abTests) &&
    getCookie(WELCOME_BACK_SHOULD_UPDATE_CTA_COOKIE_NAME) === "true" &&
    !!getCookie(LAST_ACCOUNT_SLUG)
  );
};

export const setWelcomeBackCookie = () => {
  setCookie(WELCOME_BACK_SHOULD_UPDATE_CTA_COOKIE_NAME, "true", { expires: WELCOME_BACK_COOKIE_EXPIRATION });
};

export const getWelcomeBackCTAProps = ({ productId, isPricingPlanButton }) => {
  let ctaTextKey = "existingAccount.mainCta";
  const lastSlug = getCookie(LAST_ACCOUNT_SLUG);
  let platformRedirectUrl = getMondayFullUrlForSlug(lastSlug);
  if (productId && productId !== WORK_MANAGEMENT_PRODUCT_ID) {
    ctaTextKey = "existingAccount.installProduct";
    const platformProductId = getPlatformProductID(productId);
    platformRedirectUrl = `${platformRedirectUrl}/product_store/${platformProductId}`;
  } else if (isPricingPlanButton) {
    ctaTextKey = "existingAccount.pricingCta";
    // TODO update link when platform code deployed
  }

  return { ctaTextKey, platformRedirectUrl };
};

const parseSingleAccountSlugDetails = (slugsDetailsFromCookie) => {
  if (!slugsDetailsFromCookie) return {};
  // Transforms cookie structure to the new short 2 letter key strcuture (old data is still with the long key names)
  return {
    ...slugsDetailsFromCookie, // for new keys
    un: slugsDetailsFromCookie.un || slugsDetailsFromCookie.user_name,
    ue: slugsDetailsFromCookie.ue || slugsDetailsFromCookie.user_email,
    ui: slugsDetailsFromCookie.ui || slugsDetailsFromCookie.user_id,
    us: slugsDetailsFromCookie.us || slugsDetailsFromCookie.user_serial_number,
    up: slugsDetailsFromCookie.up || slugsDetailsFromCookie.user_image || DEFAULT_PLATFORM_USER_IMAGE_SOURCE,
    an: slugsDetailsFromCookie.an || slugsDetailsFromCookie.account_name,
    ac: slugsDetailsFromCookie.ac || slugsDetailsFromCookie.account_created,
    ai: slugsDetailsFromCookie.ai || slugsDetailsFromCookie.account_id,
    sl: slugsDetailsFromCookie.sl || slugsDetailsFromCookie.slug,
    lu: slugsDetailsFromCookie.lu || slugsDetailsFromCookie.last_used
  };
};
